import { CURRENCIES, MAX_MINOR, type CurrencyCode } from "./money";

/**
 * Plain (non-localized) minor-unit formatting and parsing.
 *
 * `formatMinor` in money.ts is for humans (Intl, currency symbol, grouping). These two are for
 * machines: pay-link URLs (paylinks.ts) and CSV round-trips (csv.ts) need a stable, locale-free
 * decimal string with exactly the currency's minor digits and no separators.
 */

/** 1250 USD → "12.50"; 1250 JPY → "1250"; −500 USD → "-5.00". */
export function formatMinorPlain(amountMinor: number, code: CurrencyCode): string {
  const digits = CURRENCIES[code].minorDigits;
  const value = Number.isFinite(amountMinor) ? Math.trunc(amountMinor) : 0;
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (digits === 0) return `${sign}${abs}`;
  const unit = 10 ** digits;
  const whole = Math.floor(abs / unit);
  const frac = abs % unit;
  return `${sign}${whole}.${String(frac).padStart(digits, "0")}`;
}

/**
 * Parse a major-unit string as typed in a spreadsheet ("4.50", "1200") into integer minor units.
 * Returns null for anything that is not a plain decimal number in range (S-009: reject, never coerce).
 */
export function parseMajorToMinor(text: string, code: CurrencyCode): number | null {
  const raw = String(text).trim();
  if (!raw) return null;
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return null;
  const digits = CURRENCIES[code].minorDigits;
  const neg = raw.startsWith("-");
  const body = neg ? raw.slice(1) : raw;
  const dot = body.indexOf(".");
  const wholePart = dot === -1 ? body : body.slice(0, dot);
  const fracPart = dot === -1 ? "" : body.slice(dot + 1);
  // Reject more precision than the currency can hold rather than silently rounding money away.
  if (fracPart.replace(/0+$/, "").length > digits) return null;
  const padded = (fracPart + "0".repeat(digits)).slice(0, digits);
  const combined = `${wholePart}${padded}`;
  if (combined.length > 15) return null;
  const minor = Number.parseInt(combined || "0", 10);
  if (!Number.isSafeInteger(minor)) return null;
  const signed = neg ? -minor : minor;
  if (signed > MAX_MINOR || signed < -MAX_MINOR) return null;
  return signed;
}
